import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import InstallPWAButton from "../InstallPWAButton";

function BrandLogo({ size = 40 }) {
  return (
    <div className="flex items-center gap-2 select-none">
      <svg width={size} height={size} viewBox="0 0 64 64" aria-label="PTfinderNow logo" role="img">
        <defs>
          <linearGradient id="pinGrad" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#16A3FF" />
            <stop offset="50%" stopColor="#3B82F6" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
          <linearGradient id="personGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#E6F3FF" />
            <stop offset="100%" stopColor="#BBD8FF" />
          </linearGradient>
          <filter id="softShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.25" />
          </filter>
        </defs>
        <path d="M32 2C20 2 10 12 10 24c0 16 22 36 22 36s22-20 22-36C54 12 44 2 32 2z" fill="url(#pinGrad)" filter="url(#softShadow)" />
        <circle cx="32" cy="23" r="9" fill="url(#personGrad)" />
        <path d="M20 38c3.5-4.5 7.7-6.7 12-6.7S40.5 33.5 44 38c.6.8.9 1.8.9 2.8v2.2H19.1v-2.2c0-1 .3-2 0.9-2.8z" fill="url(#personGrad)" />
        <circle cx="32" cy="23" r="12" fill="none" stroke="white" strokeOpacity="0.9" strokeWidth="2" />
      </svg>
      <div className="leading-tight">
        <div className="flex items-baseline gap-2">
          <span className="text-[22px] md:text-[26px] font-black tracking-tight text-slate-900">PT</span>
          <span className="text-[20px] md:text-[22px] font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
            finder<span className="font-extrabold">Now</span>
          </span>
        </div>
      </div>
    </div>
  );
}

const isIOS = () =>
  typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
const inStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={[
        "sticky top-0 z-50 transition-all duration-300",
        "backdrop-blur-md border-b",
        scrolled
          ? "bg-white/80 border-slate-200 shadow-[0_6px_30px_-12px_rgba(30,64,175,0.25)]"
          : "bg-white/60 border-slate-100 shadow-sm",
      ].join(" ")}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex h-16 md:h-18 items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <BrandLogo />
            <span className="sr-only">PTfinderNow</span>
          </Link>

          {/* Desktop nav (includes install button) */}
          <nav className="hidden md:flex items-center gap-8">
            {[
              { label: "Home", to: "/" },
              { label: "About", to: "/About" },
              { label: "Explore", to: "/Coaches/Search" },
            ].map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="relative text-[15px] font-medium text-slate-700 hover:text-blue-700 transition group"
              >
                {item.label}
                <span className="absolute left-0 -bottom-1 h-[2px] w-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 transition-all duration-300 group-hover:w-full" />
              </Link>
            ))}

            <Link to="/Login" className="text-blue-700 font-semibold hover:underline underline-offset-4">
              Login
            </Link>

            <Link
              to="/joinus"
              className="relative inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-2 text-white font-semibold shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-[1px]"
            >
              Join as a PT
              <span className="h-2 w-2 rounded-full bg-white/80 ring-2 ring-white/60 animate-ping" />
            </Link>

            {/* Install button visible on desktop */}
            <InstallPWAButton className="px-3 py-2 rounded bg-sky-600 text-white" />
          </nav>

          {/* Mobile toggle */}
          <button
            className="md:hidden inline-flex items-center justify-center rounded-md p-2 hover:bg-slate-100 transition"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <XMarkIcon className="h-7 w-7 text-slate-800" /> : <Bars3Icon className="h-7 w-7 text-slate-800" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer (now includes install action) */}
      <div
        className={[
          "md:hidden overflow-hidden transition-[max-height,opacity] duration-300",
          menuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0",
        ].join(" ")}
      >
        <div className="px-4 sm:px-6 pb-4 pt-2 bg-white/90 backdrop-blur-xl border-t border-slate-200 shadow-inner">
          <div className="flex flex-col gap-3">
            {[
              { label: "Home", to: "/" },
              { label: "About", to: "/About" },
              { label: "Explore", to: "/Coaches/Search" },
            ].map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className="rounded-lg px-3 py-2 text-slate-700 font-medium hover:bg-sky-50 hover:text-blue-700 transition"
              >
                {item.label}
              </Link>
            ))}

            <div className="flex items-center justify-between gap-3 pt-1">
              <Link
                to="/Login"
                onClick={() => setMenuOpen(false)}
                className="text-blue-700 font-semibold hover:underline underline-offset-4"
              >
                Login
              </Link>

              <Link
                to="/joinus"
                onClick={() => setMenuOpen(false)}
                className="inline-flex w-auto items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-2 text-white font-semibold shadow-lg hover:shadow-xl transition"
              >
                Join us PT
              </Link>
            </div>

            {/* Install button for mobile */}
            <div className="pt-2">
              {isIOS() && !inStandalone() ? (
                <button
                  onClick={() =>
                    alert("On iPhone: open in Safari → Share → Add to Home Screen to install.")
                  }
                  className="w-full rounded-xl bg-sky-600 px-4 py-2 text-white font-semibold"
                >
                  How to install on iPhone
                </button>
              ) : (
                <InstallPWAButton
                  className="w-full rounded-xl bg-sky-600 px-4 py-2 text-white font-semibold"
                  onClick={() => setMenuOpen(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
    </header>
  );
}
